'use client'

import { useEffect, useState } from 'react'
import { Bell, BellRing } from 'lucide-react'
import { enablePush, pushSupported, pushPermission, recordPromptEvent, CAUSE_EVENT, type PromptEvent } from '@/lib/push/enablePush'

/**
 * Browser push toggle: the DELIBERATE path, for someone who has gone looking for the setting.
 * The contextual soft ask (PushPrompt) is what reaches everyone else.
 *
 * ⚠ SUBSCRIBE LOGIC LIVES IN lib/push/enablePush.ts, not here. It used to be inline, and the soft
 * prompt needed the same thing; two copies of a service-worker + permission + subscribe sequence
 * would drift, and the failure would be silent (permission granted, subscription never stored).
 *
 * ⚠ A DENIED PERMISSION CANNOT BE RE-PROMPTED. The old version rendered a dead "Blocked" label,
 * which tells a user their button is broken and nothing else. There is no API to reopen the native
 * dialog, so the only honest thing is to say where the browser setting is.
 */
export default function PushNotificationToggle({ className = '' }: { className?: string }) {
  const [supported, setSupported] = useState(false)
  const [on, setOn] = useState(false)
  const [busy, setBusy] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    const ok = pushSupported()
    setSupported(ok)
    if (!ok) return
    setBlocked(pushPermission() === 'denied')
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      const sub = reg ? await reg.pushManager.getSubscription() : null
      setOn(!!sub && Notification.permission === 'granted')
    }).catch(() => {})
  }, [])

  const enable = async () => {
    setBusy(true); setErr(null)
    let failEvent: PromptEvent = 'failed'
    const res = await enablePush((cause) => { failEvent = CAUSE_EVENT[cause] })
    setBusy(false)
    // Log the funnel outcome. The deliberate toggle previously recorded NOTHING, so most real push
    // adoption was invisible (push-permission-conversion: 5 'granted' rows against 32 subscriptions).
    // Tagged 'generic' since the toggle carries no contextual reason.
    if (res === 'granted') { recordPromptEvent('granted', 'generic'); setOn(true); return }
    if (res === 'denied') { recordPromptEvent('denied', 'generic'); setBlocked(true); return }
    recordPromptEvent(failEvent, 'generic'); setErr('failed')
  }

  const disable = async () => {
    setBusy(true); setErr(null)
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      const sub = reg ? await reg.pushManager.getSubscription() : null
      if (sub) {
        await fetch('/api/push/subscribe', {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        })
        await sub.unsubscribe()
      }
      setOn(false)
    } catch { setErr('failed') }
    setBusy(false)
  }

  if (!supported) return null

  // Blocked at the browser level: the button cannot do anything, so say what will.
  if (blocked && !on) {
    return (
      <span
        title="Your browser is blocking notifications for this site. Click the padlock or the icon at the left of the address bar, find Notifications, and set it to Allow."
        className={`inline-flex cursor-help items-center gap-1.5 rounded-lg border border-zinc-700/60 bg-zinc-900/60 px-2.5 py-1.5 text-[12px] font-semibold text-zinc-500 ${className}`}
      >
        <Bell className="h-3.5 w-3.5" />
        Blocked in browser
      </span>
    )
  }

  return (
    <button
      onClick={on ? disable : enable}
      disabled={busy}
      title={on
        ? 'Browser alerts are on - click to turn off'
        : 'Get a browser notification when someone replies to you or an alert you set fires, even with the tab closed'}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60 ${on ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20' : 'border-zinc-700/60 bg-zinc-900/60 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300'} ${className}`}
    >
      {on ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
      {busy ? '…' : on ? 'Alerts on' : err === 'failed' ? 'Try again' : 'Enable alerts'}
    </button>
  )
}
